// "Complete your order" fence system parts (snippets/fence-system-part.liquid). Ticked boxes post as
// fit_upsell="<variant id | pick>:<quantity>:<order | free | >:<key>". When any are ticked, the product
// and the parts go to /cart/add as one JSON items[] request:
//   ""     scaled by the product quantity (4 brackets per panel)
//   order  fixed quantity per order
//   free   the customer's own quantity, from fit_upsell_qty_<key>
// "pick" rows take the variant chosen in the fit_upsell_pick_<key> dropdown.
// Cut to length (snippets/cut-service.liquid) posts fit_cut={"id","cuts","properties"}: one line of
// cuts × number of lengths, carrying the lengths as line item properties.
function buildFitUpsellBody(formData) {
  const upsells = formData.getAll("fit_upsell");
  let cut = null;
  try {
    cut = formData.get("fit_cut") ? JSON.parse(formData.get("fit_cut")) : null;
  } catch (error) {
    cut = null;
  }
  formData.delete("fit_cut");
  // Copy the pick/quantity fields before removing every fit_upsell* field from the form data.
  const fields = new Map([...formData.entries()].filter(([key]) => key.startsWith("fit_upsell_")));
  const field = (name) => fields.get(name);
  [...formData.keys()]
    .filter((key) => key.startsWith("fit_upsell"))
    .forEach((key) => formData.delete(key));
  if (!upsells.length && !cut) return null;

  const quantity = parseInt(formData.get("quantity"), 10) || 1;
  const main = { id: Number(formData.get("id")), quantity };
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.*)\]$/);
    if (match && typeof value === "string") properties[match[1]] = value;
  }
  if (Object.keys(properties).length) main.properties = properties;
  if (formData.get("selling_plan")) main.selling_plan = Number(formData.get("selling_plan"));

  const items = [main];
  upsells.forEach((entry) => {
    const [ref, per, mode, key] = entry.split(":");
    const id = Number(ref === "pick" ? field(`fit_upsell_pick_${key}`) : ref);
    const count = parseInt(per, 10) || 1;
    let partQuantity = quantity * count;
    if (mode === "order") partQuantity = count;
    if (mode === "free") partQuantity = Math.max(1, parseInt(field(`fit_upsell_qty_${key}`), 10) || count);
    if (id) items.push({ id, quantity: partQuantity });
  });
  if (cut?.id && cut.cuts > 0) {
    items.push({ id: Number(cut.id), quantity: cut.cuts * quantity, properties: cut.properties || {} });
    // Tag the product's own line ("Cut into: 9 × 722mm (±5mm)" + the shared _cut_ref) so the two lines are linked.
    if (cut.main) main.properties = { ...(main.properties || {}), ...cut.main };
  }
  return { items };
}

// Part totals: scaled parts follow the product quantity (3 panels x 4 per panel = 12 brackets),
// "order" parts stay fixed, "free" parts follow their own stepper; dropdown rows take the picked
// option's price. Liquid renders every row for quantity 1 and the first option.
function updateFenceSystemTotals() {
  document.querySelectorAll(".fence-system__upsell").forEach((box) => {
    const quantityInput = document.querySelector(`input[name="quantity"][form="${box.dataset.form}"]`);
    const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
    const money = new Intl.NumberFormat(box.dataset.locale || undefined, {
      style: "currency",
      currency: box.dataset.currency,
    });
    box.querySelectorAll(".fence-system__item").forEach((item) => {
      const price = item.querySelector("[data-unit-price]");
      if (!price) return;
      const picked = item.querySelector(".fence-system__pick")?.selectedOptions[0];
      if (picked) price.dataset.unitPrice = picked.dataset.price;
      const mode = price.dataset.mode;
      const qtyPer = parseInt(price.dataset.qtyPer, 10) || 1;
      let count = quantity * qtyPer;
      if (mode === "order") count = qtyPer;
      if (mode === "free") {
        count = Math.max(1, parseInt(item.querySelector(".fence-system__qty-input")?.value, 10) || qtyPer);
      }
      price.textContent = money.format((Number(price.dataset.unitPrice) * count) / 100);
      const total = item.querySelector("[data-fence-total]");
      if (total) {
        total.hidden = mode !== "" || quantity < 2;
        total.textContent = total.hidden ? "" : ` · ${count} for ${quantity} ${price.dataset.per}s`;
      }
    });
  });
}
["input", "change"].forEach((type) =>
  document.addEventListener(
    type,
    (event) => {
      const target = event.target;
      if (target.name === "quantity" || target.closest?.(".fence-system__item")) updateFenceSystemTotals();
    },
    true, // capture: the theme stops some events bubbling
  ),
);
// −/+ on customer-set quantities. Choosing a quantity also ticks the part, so it gets added.
const tickFencePart = (element) => {
  const checkbox = element.closest(".fence-system__item")?.querySelector(".fence-system__checkbox");
  if (checkbox && !checkbox.checked) checkbox.checked = true;
};
document.addEventListener("click", (event) => {
  const button = event.target.closest?.("[data-fence-step]");
  if (!button) return;
  const input = button.parentElement.querySelector(".fence-system__qty-input");
  input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.fenceStep));
  tickFencePart(input);
  input.dispatchEvent(new Event("input", { bubbles: true }));
});
document.addEventListener("input", (event) => {
  if (event.target.classList?.contains("fence-system__qty-input")) tickFencePart(event.target);
});
// Picking a different option shows that product's image on the card.
document.addEventListener(
  "change",
  (event) => {
    if (!event.target.classList?.contains("fence-system__pick")) return;
    const option = event.target.selectedOptions[0];
    const image = event.target.closest(".fence-system__item")?.querySelector(".fence-system__media img");
    if (!option || !image || !option.dataset.image) return;
    image.removeAttribute("srcset");
    image.removeAttribute("sizes");
    image.src = option.dataset.image;
    image.alt = option.dataset.alt || "";
  },
  true,
);
// The parts are re-rendered (at quantity 1) when the variant changes.
document.addEventListener("product:variant-change", updateFenceSystemTotals);

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.minicart = document.querySelector("mini-cart");
        this.quickViewDrawer = this.closest("quick-view-drawer");
        this.eventContext = this.form.dataset.eventContext;
        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(event) {
        event.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        if (submitButton.classList.contains("loading")) {
          return;
        }

        this.handleErrorMessage();

        submitButton.setAttribute("aria-disabled", true);
        submitButton.classList.add("loading");
        this.querySelector(".button-overlay-spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        const upsellBody = buildFitUpsellBody(formData);
        const sections = this.minicart.getSectionsToRender().map((section) => section.section);
        if (upsellBody) {
          config.headers["Content-Type"] = "application/json";
          config.body = JSON.stringify({
            ...upsellBody,
            sections,
            sections_url: window.location.pathname,
          });
        } else {
          formData.append("sections", sections);
          formData.append("sections_url", window.location.pathname);
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
              this.minicart.updateSectionContents();
              return;
            }

            this.minicart.renderContents(response);
            document
              .querySelectorAll(`.fit-upsell__checkbox[form="${this.form.getAttribute("id")}"]:checked`)
              .forEach((checkbox) => (checkbox.checked = false));

            if (window.themeSettings.redirectToCart) {
              location.href = window.routes.cart_url;
            } else {
              if (this.quickViewDrawer) {
                this.quickViewDrawer.close();
                // Immediately re-add the 'drawer-open' class to avoid
                // the drawer backdrop from flashing.
                document.body.classList.add("drawer-open");
                setTimeout(() => this.minicart.open(), 200);
              } else {
                this.minicart.open(submitButton);
              }
            }

            const event = new CustomEvent("cart:item-add", {
              detail: {
                items: response.items || [response],
                context: this.eventContext,
              },
            });
            document.dispatchEvent(event);

            publish(PUB_SUB_EVENTS.cartItemAdd, {
              source: this.eventContext,
              items: response.items || [response],
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton.classList.remove("loading");
            submitButton.removeAttribute("aria-disabled");
            this.querySelector(".button-overlay-spinner").classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form-error-message-wrapper");
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form-error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
